<template>
	<BudgetChartBase
		data-testid="budget-chart"
		class-name="budget-chart__chart-time"
		:project-id="projectId"
		:retainer-period-id="retainerPeriodId"
		:loading="loading"
		show-planned
		:project="project"
		:make-y-axis-duration-options="yAxisDuration"
	/>
</template>

<script lang="ts" setup>
import { computed, reactive, toRefs, watch } from 'vue';
import BudgetChartBase from './BudgetChartBase.vue';
import { yAxisDuration } from '@/lib/highchartsUtils';
import { useRefetchOnSocketMessage } from '@/lib/apollo/apolloTools';
import {
	APOLLO_PARAMS_PERSISTED_QUERY,
	usePersistedQueryLoading,
} from '@/lib/apollo/apollo';
import {
	type BudgetChartQueryVariables,
	type Project,
	ProjectProgressGroupBy,
	type RetainerPeriod,
	useBudgetChartQuery,
} from '@/generated/graphql';
import { type ChartProgressGroup, type ChartProject } from '../types';
import { SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING } from '@/features/Project/constants';

export interface BudgetChartProps {
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
}

const props = defineProps<BudgetChartProps>();

const { projectId, retainerPeriodId } = toRefs(props);

const variables = reactive<BudgetChartQueryVariables>({
	projectInput: {
		projectId: Number(props.projectId),
	},
	projectProgressInput: {
		groupBy: ProjectProgressGroupBy.Roles,
		retainerPeriodId: retainerPeriodId.value,
	},
});

const { result, loading } = useRefetchOnSocketMessage(
	usePersistedQueryLoading(
		useBudgetChartQuery(variables, APOLLO_PARAMS_PERSISTED_QUERY)
	),
	{
		messages: SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING,
		filter: (message) => {
			return String(message.data.project_id) === props.projectId;
		},
	}
);

const project = computed<ChartProject | undefined>(() => {
	if (result?.value?.Project) {
		return {
			...result.value.Project,
			progressData: (result.value.Project.progressData || [])
				.map(
					(elem): ChartProgressGroup => ({
						budgetValue: elem.timeBudget,
						groupName: elem.groupName,
						plannedValue: elem.plannedTime,
						actualValue: elem.actualTime,
						forecastedValue: elem.forecastedTime + elem.actualTime,
						actualCost: 0,
						actualRevenue: 0,
						bookingsCost: 0,
						costBudget: 0,
						forecastedCost: 0,
						forecastedRevenue: 0,
						monetaryBudget: 0,
						tasksCost: 0,
					})
				)
				.filter(
					(elem) =>
						elem.budgetValue ||
						elem.plannedValue ||
						elem.actualValue ||
						elem.forecastedValue
				),
		};
	}
	return undefined;
});

watch(retainerPeriodId, (retainerPeriodId) => {
	variables.projectProgressInput.retainerPeriodId = retainerPeriodId;
});
</script>
